<template>
    <form name="form-speaker" id="form-speaker" class="event-detail-form" @submit="onSubmit">
        <input type="hidden" name="id" v-model="model.id">
        <input type="hidden" name="eventId" v-model="model.eventId">
        <input type="hidden" name="imageName" v-model="model.imageName">
        <input type="hidden" name="imageNameOrigin" v-model="model.imageNameOrigin">

        <p class="required-note"><span>*</span> Required fields</p><div class="form-row event-detail-sections">

            <div class="col-md-6 event-detail-section session-details">
                <div class="section-heading"><div><p>SESSION</p><h2>Session details</h2></div><i class="pi pi-calendar" aria-hidden="true"></i></div>
                <div class="form-group">
                    <label for="name">Session name <span>*</span></label>
                    <InputText class="form-control" v-model="model.name" id="name"></InputText>
                </div>

                <div class="form-group">
                    <label for="description">Description <span>*</span></label>
                    <Textarea class="form-control" v-model="model.description" id="description" rows="9"></Textarea>
                </div>
            </div>

            <div class="col-md-6 event-detail-section session-settings">
                <div class="section-heading"><div><p>LOCATION AND FORMAT</p><h2>Session settings</h2></div><i class="pi pi-map-marker" aria-hidden="true"></i></div>
                <div class="form-group">
                    <label for="locationName">Location name <span>*</span></label>
                    <InputText class="form-control" v-model="model.locationName" id="locationName"></InputText>
                </div>

                <div class="form-group">
                  <label for="icon">Session image</label>
                  <FileUpload mode="advanced" name="file" :multiple="false" accept="image/*" :file-limit="1" :auto="true" v-model="model.uploadFile" :url="uploadUrl" @error="onUploadError" @upload="onUploadSuccess"></FileUpload>
                  <!-- A just-uploaded file already shows its own thumbnail in the
                       uploader above, so repeating it here would be noise. What the
                       uploader does not say is that "Completed" only means the file
                       reached the server — it is attached to the session on save. -->
                  <p v-if="model.imageName" class="session-image-note">New image. Save the session to apply it.</p>
                  <div v-else-if="imagePreviewUrl" class="session-image-preview" :class="{ 'is-missing': imageMissing }">
                    <img v-if="!imageMissing" :src="imagePreviewUrl" alt="Session image" @error="imageMissing = true">
                    <span v-else class="preview-thumb-missing" aria-hidden="true"><i class="pi pi-image"></i></span>
                    <span v-if="imageMissing">This session's image file is no longer on the server. Upload one to replace it.</span>
                    <span v-else>Current image</span>
                  </div>
                </div>

                <div class="form-group">
                     <label>Main event session?</label>
                    <div class="flex items-center gap-2">
                        <RadioButton v-model="model.category" inputId="mainEventYes" :value="1" />
                        <label for="mainEventYes">Yes</label>
                    </div>
                    <div class="flex items-center gap-2">
                        <RadioButton v-model="model.category" inputId="mainEventNo" :value="2" />
                        <label for="mainEventNo">No</label>
                    </div>
                </div>

            </div>


            <div class="col-md-6 event-detail-section session-schedule">
                <div class="section-heading"><div><p>SCHEDULE</p><h2>Session timing</h2></div><i class="pi pi-clock" aria-hidden="true"></i></div>
                <div class="row">
                    <div class="col">
                        <div class="form-group">
                            <label for="startDate">Start date <span>*</span></label>
                            <div class="input-group">
                                <Calendar v-model="model.startDate" :showIcon="false" showTime input-class="form-control" dateFormat="yy-mm-dd" :showSeconds="false"/>
                                <span class="input-group-text bg-primary text-white"><i class="pi pi-calendar"></i></span>
                            </div>
                        </div>
                    </div>
                    <div class="col">
                        <div class="form-group">
                            <label for="endDate">End date <span>*</span></label>
                            <div class="input-group">
                                <Calendar v-model="model.endDate" :showIcon="false" showTime input-class="form-control" dateFormat="yy-mm-dd" :showSeconds="false"/>
                                <span class="input-group-text bg-primary text-white"><i class="pi pi-calendar"></i></span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
        </div>

        <!-- Save only covers the session fields above; topics are saved from their
             own dialogs, so the buttons sit here rather than under the topic list. -->
        <div class="form-actions">
            <button type="button" class="btn btn-outline-secondary" @click="onCancel">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">Save changes</button>
        </div>

        <section class="topics-section" v-if="model.category === 1 && !isNew">
            <div class="section-heading">
                <div>
                    <p>SPEAKERS</p>
                    <h2>Topics</h2>
                    <span class="section-note">Changes to topics are saved right away.</span>
                </div>
                <button class="btn btn-primary" type="button" @click="onAddSpeaker"><i class="pi pi-plus" aria-hidden="true"></i> Add topic</button>
            </div>

            <div class="table-container">
            <table class="table topics-table">

              <thead>
                  <th class="col-number">No</th>
                  <th>Speaker</th>
                  <th>Topic</th>
                  <th class="col-priority">Priority</th>
                  <th class="col-center">Presentation file</th>
                  <th class="col-center">Action</th>
              </thead>

              <tbody>
                  <tr v-for="(speaker, idx) in model.speakers" :key="speaker.eventDetailSpeakerId">
                      <td class="order-number">{{ idx + 1 }}</td>
                      <td class="text-left">
                        <span v-if="speaker.isSpeakerTba" class="speaker-tba" v-tooltip="'Speaker not confirmed yet. Edit the topic to assign one.'">TBA</span>
                        <template v-else>{{ speaker.name }}</template>
                      </td>
                      <td class="text-left">{{ speaker.topic }}</td>
                      <td class="col-priority">{{ priorityLabel(speaker.orderNumber) }}</td>
                      <td class="actions">
                          <a v-if="hasPresentationFile(speaker)" v-tooltip="'Download presentation file'" class="btn btn-outline-secondary"  :href="getDownloadUrl(speaker.presentationFile)" target="_blank"><i class="pi pi-download"></i></a>

                          <button v-if="!hasPresentationFile(speaker)" type="button" class="btn btn-outline-secondary" @click="onUploadPresentationFile(speaker)" v-tooltip="'Upload presentation file'">
                              <i class="pi pi-upload"></i>
                          </button>

                          <button v-if="hasPresentationFile(speaker)" type="button" class="btn btn-outline-secondary"  @click="onRemovePresentationFile(speaker)" v-tooltip="'Remove presentation file'">
                              <i class="pi pi-times"></i>
                          </button>
                      </td>
                      <td class="actions">
                        <button type="button" class="btn btn-outline-secondary" @click="onEditSpeaker(speaker)" v-tooltip="'Edit topic'"><i class="pi pi-pencil"></i></button>
                        <button type="button" class="btn btn-outline-secondary" @click="onRemoveSpeaker(speaker)" v-tooltip="'Remove topic'"><i class="pi pi-trash"></i></button>
                      </td>
                  </tr>
                  <tr v-if="model.speakers.length === 0">
                    <td colspan="6" class="topics-empty">No topics yet. Add one to list it under this session.</td>
                  </tr>
              </tbody>
            </table>
            </div>
        </section>

    </form>

    <ConfirmDialog></ConfirmDialog>

    <Dialog v-model:visible="showPresentationFileUploadDialog" modal header="Upload Presentation File" :style="{ width: '50rem' }" :breakpoints="{ '1199px': '75vw', '575px': '90vw' }">
        <PresentationFileUploadForm :speaker="selectedSpeaker" @onCancelUploadPresentationFile="onCancelUploadPresentationFile" @onSubmitUploadPresentationFile="onSubmitUploadPresentationFile"></PresentationFileUploadForm>
    </Dialog>

    <Dialog v-model:visible="showAddSpeakerDialog" modal :header="selectedSpeaker.eventDetailSpeakerId ? 'Edit topic' : 'Add topic'" :style="{ width: '50rem' }" :breakpoints="{ '1199px': '75vw', '575px': '90vw' }" :contentStyle="{ overflow: 'visible' }">
        <EventDetailAddSpeakerForm :event-detail="model" :speaker="selectedSpeaker" @onCancelAddSpeaker="onCancelAddSpeaker" @onSubmitAddSpeaker="onSubmitAddSpeaker"></EventDetailAddSpeakerForm>
    </Dialog>

</template>

<script>
import {defineComponent} from 'vue'
import InputText from "primevue/inputtext";
import Textarea from "primevue/textarea";
import apiService from "@/services/ApiService";
import Dialog from 'primevue/dialog';
import PresentationFileUploadForm from "@/components/event/PresentationFileUploadForm.vue";
import EventDetailAddSpeakerForm from "@/components/event/EventDetailAddSpeakerForm.vue";
import FileUpload from "primevue/fileupload";
import Calendar from "primevue/calendar";
import RadioButton from "primevue/radiobutton";

const UNSET_PRIORITY = 999;


export default defineComponent({
    name: "EventForm",
    components: {
      EventDetailAddSpeakerForm,
        InputText, Textarea, Dialog, PresentationFileUploadForm, FileUpload, Calendar, RadioButton
    },
    props: {
        id: {
            type: String,
            required: false
        },
        isNew: {
            type: Boolean,
            required: true
        }
    },
    data() {
      return {
        model: {
          id: '',
          name: '',
          description: '',
          startDate: '',
          endDate: '',
          locationName: '',
          status: '',
          category: 2,
          imageName: '',
          imageNameOrigin: '',
          uploadFile: '',
          speakers: [{id: '', name: '', topic: '', presentationFile: null, presentationFileOrigin: ''}],
          eventId: ''
        },
        saving: false,
        imageMissing: false,
        downloadUrl: '',
        uploadUrl: '',
        showPresentationFileUploadDialog: false,
        showAddSpeakerDialog: false,
        selectedSpeaker: {
          id: '',
          name: '',
          eventDetailId: '',
          eventDetailName: '',
          topic: '',
          eventDetailSpeakerId: '',
          orderNumber: ''
        }
      }
    },
    computed: {
        // GET /eventdetail/{id} returns the stored image as `imageNames` (a list),
        // while a fresh upload sets `imageName`. Loading the form overwrites the
        // whole model with the response, so only the list survives — which is why
        // an already-uploaded image used to show nothing here. A just-uploaded file
        // wins, so the preview follows what the form will actually save.
        imagePreviewUrl() {
            const file = this.model.imageName || (this.model.imageNames || [])[0];
            return file ? `${apiService.defaults.baseURL}/file/${file}` : '';
        }
    },
    watch: {
        imagePreviewUrl() {
            this.imageMissing = false;
        }
    },
    methods: {
        onSubmit(event) {
            event.preventDefault();
            this.saving = true;

            // Prepare model for submission with proper date formatting
            const submitModel = { ...this.model };
            
            // Convert dates to string format without timezone conversion
            if (submitModel.startDate) {
                if (submitModel.startDate instanceof Date) {
                    submitModel.startDate = this.getDateWithoutTimezone(submitModel.startDate);
                }
            }
            if (submitModel.endDate) {
                if (submitModel.endDate instanceof Date) {
                    submitModel.endDate = this.getDateWithoutTimezone(submitModel.endDate);
                }
            }

            let url = '/eventdetail/edit';
            let successMessage = 'Event has been updated';
            let errorMessage = 'Failed to update event detail';
            if(this.isNew){
                url = '/eventdetail/add';
                successMessage = 'Event has been added';
                errorMessage = 'Failed to add event detail';
            }

            apiService.post(url, submitModel)
                .then(() => {
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: successMessage,
                        life: 5000
                    });
                })
                .catch(error => {
                    console.log(error);
                    this.$toast.add({
                        severity: 'error',
                        summary: errorMessage,
                        detail: error.response.data.Message,
                        life: 5000
                    });
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        onCancel() {
            this.$router.push('/event-active')
        },
        onUploadPresentationFile(speaker){
            console.log(speaker);
            this.selectedSpeaker.id = speaker.id;
            this.selectedSpeaker.name = speaker.name;
            this.selectedSpeaker.topic = speaker.topic;
            this.selectedSpeaker.eventDetailId = this.model.id;
            this.selectedSpeaker.eventDetailName = this.model.name;
            this.selectedSpeaker.eventDetailSpeakerId = speaker.eventDetailSpeakerId;
            this.showPresentationFileUploadDialog = true;
        },
        onSubmitUploadPresentationFile(){
            this.showPresentionFileUploadDialog = false;
            this.selectedSpeaker = {
                id: '',
                name: '',
                eventDetailId: '',
                eventDetailName: '',
                topic: '',
                eventDetailSpeakerId: '',
                orderNumber: ''
            };
            this.getSpeakersByEventDetailId(this.model.id);
        },
        onRemovePresentationFile(speaker){
            console.log(speaker);

            let url = '/eventdetail/remove-presentation-file';
            let successMessage = 'Presentation file has been removed';
            let errorMessage = 'Failed to remove presentation file';

            var param = {
                eventDetailId: this.model.id,
                id: speaker.eventDetailSpeakerId
            }

            apiService.post(url, param)
                .then(() => {
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: successMessage,
                        life: 5000
                    });
                })
                .catch(error => {
                    console.log(error);
                    this.$toast.add({
                        severity: 'error',
                        summary: errorMessage,
                        detail: error.response.data.Message,
                        life: 5000
                    });
                })
                .finally(() => {
                    console.log('final');
                    this.saving = false;
                    this.getSpeakersByEventDetailId(this.model.id);
                });
        },
        getEventById(id) {
            apiService.get(`/eventdetail/${id}`)
                .then(response => {
                    this.model = response.data;
                    // Convert date strings to Date objects without timezone conversion
                    if (this.model.startDate) {
                        this.model.startDate = this.parseDateWithoutTimezone(this.model.startDate);
                    }
                    if (this.model.endDate) {
                        this.model.endDate = this.parseDateWithoutTimezone(this.model.endDate);
                    }
                }).catch(error => {
                console.log(error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Failed to get event detail',
                    detail: error.response.data.Message,
                    life: 5000
                });
            });
        },
        // order_number defaults to 999 in the database, meaning "not set". Showing
        // it on every row buries the few topics that were deliberately ordered.
        priorityLabel(orderNumber) {
            return orderNumber === UNSET_PRIORITY ? '' : orderNumber;
        },
        hasPresentationFile(speaker){
            return speaker.presentationFile !== null && speaker.presentationFile !== '';
        },
        getDownloadUrl(fileName){
            return  `${apiService.defaults.baseURL}/file/${fileName}`;
        },
        onCancelUploadPresentationFile(){
            this.showPresentationFileUploadDialog = false;
            this.selectedSpeaker = {
                id: '',
                name: '',
                eventDetailId: '',
                eventDetailName: '',
                topic: '',
                eventDetailSpeakerId: '',
                orderNumber: ''
            };
        },
        onAddSpeaker(){
          this.showAddSpeakerDialog = true;
          this.selectedSpeaker = {
            id: '',
            name: '',
            eventDetailId: '',
            eventDetailName: '',
            topic: '',
            eventDetailSpeakerId: '',
            orderNumber: this.nextPriority()
          };
        },
        // A new topic goes right after the highest priority already set in this
        // session. Unset topics (999) are left out, otherwise every suggestion
        // would be 1000 and land below them.
        nextPriority() {
          const priorities = this.model.speakers
              .map(speaker => speaker.orderNumber)
              .filter(orderNumber => orderNumber !== UNSET_PRIORITY);
          return priorities.length ? Math.max(...priorities) + 1 : 1;
        },
        onSubmitAddSpeaker(){
          this.showAddSpeakerDialog = false;
          this.getSpeakersByEventDetailId(this.model.id);
        },
        onCancelAddSpeaker(){
          console.log('onCancelAddSpeaker');
          this.showAddSpeakerDialog = false;
          this.selectedSpeaker = {
            id: '',
            name: '',
            eventDetailId: '',
            eventDetailName: '',
            topic: '',
            eventDetailSpeakerId: ''
          };
        },
        onEditSpeaker(speaker) {
          this.showAddSpeakerDialog = true;
          console.log(speaker);

          this.selectedSpeaker.id = speaker.isSpeakerTba ? '' : speaker.id;
          this.selectedSpeaker.name = speaker.name;
          this.selectedSpeaker.topic = speaker.topic;
          this.selectedSpeaker.eventDetailId = this.model.id;
          this.selectedSpeaker.eventDetailName = this.model.name;
          this.selectedSpeaker.eventDetailSpeakerId = speaker.eventDetailSpeakerId;
          this.selectedSpeaker.orderNumber = speaker.orderNumber;
        },
        onRemoveSpeaker(data) {
          this.$confirm.require({
            message: 'Are you sure you want to remove this topic ?',
            header: 'Confirmation',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
              this.doRemoveSpeaker(data);
            }
          });
        },
        doRemoveSpeaker(speaker){
          console.log(speaker);

          let url = '/eventdetail/remove-speaker';
          let successMessage = 'Topic has been removed';
          let errorMessage = 'Failed to remove topic';

          var param = {
            eventDetailId: this.model.id,
            speakerId: speaker.id,
            id: speaker.eventDetailSpeakerId
          }

          apiService.post(url, param)
              .then(() => {
                this.$toast.add({
                  severity: 'success',
                  summary: 'Success',
                  detail: successMessage,
                  life: 5000
                });
              })
              .catch(error => {
                console.log(error);
                this.$toast.add({
                  severity: 'error',
                  summary: errorMessage,
                  detail: error.response.data.Message,
                  life: 5000
                });
              })
              .finally(() => {
                this.saving = false;
                this.getSpeakersByEventDetailId(this.model.id);
              });
        },
        onUploadSuccess(event){
          let response = JSON.parse(event.xhr.response);
          console.log(event.xhr.response);
          this.model.imageName = response.fileName;
          this.model.imageNameOrigin = response.fileNameOrigin;
        },
        onUploadError(event){
          let response = JSON.parse(event.xhr.response);
          this.$toast.add({
            severity: 'error',
            summary: 'Failed to upload file',
            detail: response.Message,
            life: 5000
          });
        },
        getSpeakersByEventDetailId(id) {
            apiService.get(`/eventdetail/${id}/speakers`)
                .then(response => {
                    this.model.speakers = response.data;
                }).catch(error => {
                console.log(error);
                this.$toast.add({
                    severity: 'error',
                    summary: 'Failed to get event detail speakers',
                    detail: error.response.data.Message,
                    life: 5000
                });
            });
        },
        onStartDateSelect(value) {
            // Get date without timezone conversion
            if (value) {
                this.model.startDate = this.getDateWithoutTimezone(value);
            }
        },
        onEndDateSelect(value) {
            // Get date without timezone conversion
            if (value) {
                this.model.endDate = this.getDateWithoutTimezone(value);
            }
        },
        getDateWithoutTimezone(date) {
            // Convert date to ISO string but keep local time (ignore timezone)
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        },
        parseDateWithoutTimezone(dateString) {
            // Parse date string without timezone conversion
            if (!dateString) return null;
            
            // Handle different date string formats
            let cleanDateString = dateString.replace('T', ' ').replace('Z', '');
            
            // If the string contains timezone info, remove it
            if (cleanDateString.includes('+') || cleanDateString.includes('-')) {
                cleanDateString = cleanDateString.split(/[+-]/)[0];
            }
            
            // Parse the date components manually to avoid timezone conversion
            const parts = cleanDateString.split(/[\s:-]/);
            if (parts.length >= 6) {
                return new Date(
                    parseInt(parts[0]), // year
                    parseInt(parts[1]) - 1, // month (0-based)
                    parseInt(parts[2]), // day
                    parseInt(parts[3]) || 0, // hours
                    parseInt(parts[4]) || 0, // minutes
                    parseInt(parts[5]) || 0  // seconds
                );
            } else if (parts.length >= 3) {
                return new Date(
                    parseInt(parts[0]), // year
                    parseInt(parts[1]) - 1, // month (0-based)
                    parseInt(parts[2]) // day
                );
            }
            
            return new Date(dateString);
        },
    },
    mounted() {
        this.model.eventId = this.$route.query.eventId;
        this.uploadUrl = `${apiService.defaults.baseURL}/file/upload`;
        if(!this.isNew){
            this.getEventById(this.id);
        }
    }
})
</script>

<style scoped>
.event-detail-form{max-width:1280px}.required-note{color:#5f6570;font-size:.875rem;margin-bottom:1rem}.required-note span,.event-detail-form label span{color:#c62828;font-weight:700}.event-detail-sections{display:flex;gap:10px;margin-left:0;margin-right:0}.event-detail-sections>.event-detail-section{background:#fff;border:1px solid #e2e6ec;border-radius:.65rem;box-shadow:0 .15rem .65rem rgba(58,59,69,.05);margin-bottom:0;padding:1.5rem}.session-details,.session-settings{flex:0 0 calc(50% - 5px);max-width:calc(50% - 5px)}.session-schedule{flex:0 0 100%;max-width:100%;margin-top:0}.section-heading{align-items:flex-start;border-bottom:1px solid #edf0f4;display:flex;justify-content:space-between;margin-bottom:1.5rem;padding-bottom:1rem}.section-heading p{color:#426cc6;font-size:.7rem;font-weight:700;letter-spacing:.08em;margin:0 0 .25rem}.section-heading h2{color:#2b2f36;font-size:1.15rem;font-weight:600;margin:0}.section-heading>.pi{color:#426cc6;font-size:1.2rem}.event-detail-form .form-group label{color:#343a40;font-size:.9rem;font-weight:600}.event-detail-form .p-fileupload{margin-top:.4rem}
/* Square, not the round crop used for speaker portraits: this image is reused
   across sessions as a category icon and is shown square on the timeline. */
.session-image-preview{align-items:center;color:#5a6270;display:flex;font-size:.8rem;gap:.65rem;line-height:1.4;margin-top:.85rem}
.session-image-note{color:#5a6270;font-size:.8rem;line-height:1.4;margin:.85rem 0 0}
.session-image-preview img,.preview-thumb-missing{border:1px solid #e2e6ec;border-radius:.35rem;flex:none;height:3rem;object-fit:cover;width:3rem}
.preview-thumb-missing{align-items:center;background:#f7f9fc;color:#9aa2b1;display:flex;justify-content:center}
.session-image-preview.is-missing{color:#b42318}
.session-image-preview.is-missing .preview-thumb-missing{background:#fdecea;border-color:#f3c6c1;color:#b42318}
.speaker-tba{background:#fffbeb;border-radius:.375rem;color:#92400e;display:inline-block;font-size:.75rem;font-weight:600;padding:.2rem .5rem}
/* Same card treatment as the three sections above, so the topic list reads as
   part of this session rather than something floating under the form. */
.topics-section{background:#fff;border:1px solid #e2e6ec;border-radius:.65rem;box-shadow:0 .15rem .65rem rgba(58,59,69,.05);padding:1.5rem}
.topics-section .section-heading{align-items:center;border-bottom:0;margin-bottom:1rem;padding-bottom:0}
.section-note{color:#5a6270;display:block;font-size:.8rem;margin-top:.25rem}
.topics-section .table-container{margin:0 -.75rem}
.topics-table{margin-bottom:0}
.topics-table th{text-align:left}
.topics-table th.col-number,.topics-table .col-priority{text-align:right;width:6rem}
.topics-table th.col-center{text-align:center}
.topics-empty{color:#5a6270;padding:1.5rem .75rem;text-align:center}
.form-actions{display:flex;gap:.5rem;justify-content:flex-end;margin:1.25rem 0}
@media(max-width:767.98px){.event-detail-sections{gap:10px}.session-details,.session-settings{flex-basis:100%;max-width:100%}}
</style>
